import { supabase } from '@/services/supabase'
import { getCategoriesCache, setCategoriesCache, clearCategoriesCache, markLastUpdate } from '@/utils/cacheService'
import { CATALOG_LAYOUT_TYPES } from '@/data/catalog-layouts'

// Định nghĩa kiểu dữ liệu cho Category
export interface Category {
  id: string
  name: string
  description?: string
  image_url?: string
  slug?: string
  icon?: string
  color?: string
  content_type?: ContentType // Loại nội dung mà danh mục áp dụng
  layout_type?: string // Loại layout cho danh mục (chỉ áp dụng cho khóa học)
  layout_options?: Record<string, any> // Các tùy chọn bổ sung cho layout
  created_at?: string
  updated_at?: string
}

// Enum cho các loại nội dung
export enum ContentType {
  ALL = 'all',         // Áp dụng cho tất cả các loại nội dung
  COURSE = 'course',   // Chỉ áp dụng cho khóa học
  DIAGRAM = 'diagram', // Chỉ áp dụng cho sơ đồ
  POST = 'post'        // Chỉ áp dụng cho bài viết
}

// Lấy tất cả danh mục - sử dụng cache từ cacheService
export async function getAllCategories(contentType?: ContentType): Promise<Category[]> {
  console.log(`Fetching categories for content type: ${contentType || 'all'}`)
  
  // Tạo cache key dựa trên content type
  const cacheKey = contentType ? `${contentType}_categories` : 'all_categories'
  
  // Kiểm tra xem có dữ liệu trong cache không
  const cachedCategories = getCategoriesCache(cacheKey)
  if (cachedCategories) {
    console.log('Using categories from cache')
    return cachedCategories
  }
  
  // Không có trong cache, lấy từ database
  console.log('Fetching categories from database directly')
  try {
    let query = supabase
      .from('categories')
      .select('*')
    
    // Nếu có content_type, lọc theo content_type
    if (contentType) {
      query = query.or(`content_type.eq.${contentType},content_type.eq.${ContentType.ALL}`)
    }
    
    const { data, error } = await query.order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching categories:', error)
      throw error
    }

    // Đảm bảo tất cả các danh mục đều có content_type và layout_type
    const processedData = (data || []).map(category => ({
      ...category,
      content_type: category.content_type || ContentType.ALL,
      layout_type: category.layout_type || CATALOG_LAYOUT_TYPES.DEFAULT
    }));

    // Lưu kết quả vào cache
    if (processedData.length > 0) {
      console.log('Saving categories to cache')
      setCategoriesCache(processedData, cacheKey)
    }

    return processedData as Category[]
  } catch (error) {
    console.error('Error in getAllCategories:', error)
    return []
  }
}

// Lấy danh mục theo ID
export async function getCategoryById(id: string): Promise<Category | null> {
  console.log(`Fetching category with id: ${id}`)
  
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    if (error) {
      console.error(`Error fetching category with id ${id}:`, error)
      throw error
    }

    // Đảm bảo content_type và layout_type có giá trị
    if (data) {
      if (!data.content_type) {
        data.content_type = ContentType.ALL;
      }
      if (!data.layout_type) {
        data.layout_type = CATALOG_LAYOUT_TYPES.DEFAULT;
      }
    }

    return data
  } catch (error) {
    console.error(`Error in getCategoryById for id ${id}:`, error)
    return null
  }
}

// Tạo danh mục mới
export async function createCategory(category: Omit<Category, 'id' | 'created_at' | 'updated_at'>): Promise<Category | null> {
  console.log('Creating new category:', category)
  
  try {
    const now = new Date().toISOString()
    const newCategory = {
      ...category,
      content_type: category.content_type || ContentType.ALL,
      layout_type: category.layout_type || CATALOG_LAYOUT_TYPES.DEFAULT,
      created_at: now
      // Không thêm updated_at nếu cột không tồn tại
    }
    
    const { data, error } = await supabase
      .from('categories')
      .insert(newCategory)
      .select()
      .maybeSingle()

    if (error) {
      console.error('Error creating category:', error)
      throw error
    }

    // Xóa cache để đảm bảo dữ liệu mới nhất
    clearCategoriesCache()
    markLastUpdate()

    return data
  } catch (error) {
    console.error('Error in createCategory:', error)
    return null
  }
}

// Cập nhật danh mục
export async function updateCategory(category: Category): Promise<Category | null> {
  console.log(`Updating category with id ${category.id}:`, category)
  
  try {
    // Đảm bảo content_type và layout_type có giá trị
    const updates = {
      ...category,
      content_type: category.content_type || ContentType.ALL,
      layout_type: category.layout_type || CATALOG_LAYOUT_TYPES.DEFAULT
      // Không thêm updated_at nếu cột không tồn tại
    }
    
    const { data, error } = await supabase
      .from('categories')
      .update(updates)
      .eq('id', category.id)
      .select()
      .maybeSingle()

    if (error) {
      console.error(`Error updating category with id ${category.id}:`, error)
      throw error
    }

    // Xóa cache để đảm bảo dữ liệu mới nhất
    clearCategoriesCache()
    markLastUpdate()

    return data
  } catch (error) {
    console.error(`Error in updateCategory for id ${category.id}:`, error)
    return null
  }
}

// Xóa danh mục
export async function deleteCategory(id: string): Promise<boolean> {
  console.log(`Deleting category with id: ${id}`)
  
  try {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id)

    if (error) {
      console.error(`Error deleting category with id ${id}:`, error)
      throw error
    }

    // Xóa cache để đảm bảo dữ liệu mới nhất
    clearCategoriesCache()
    markLastUpdate()

    return true
  } catch (error) {
    console.error(`Error in deleteCategory for id ${id}:`, error)
    return false
  }
}

// Lấy danh mục theo slug hoặc ID (để hỗ trợ tương thích ngược)
export async function getCategoryBySlugOrId(slugOrId: string): Promise<Category | null> {
  if (!slugOrId) {
    console.log('getCategoryBySlugOrId called with empty slugOrId');
    return null;
  }
  
  console.log(`Looking up category by slug or id: ${slugOrId}`);
  
  try {
    // Thử tìm theo slug trước
    console.log(`First trying by slug: ${slugOrId}`);
    const { data: slugData, error: slugError } = await supabase
      .from('categories')
      .select('*')
      .eq('slug', slugOrId)
      .maybeSingle();

    if (!slugError && slugData) {
      console.log(`Found category by slug: ${slugOrId}`);
      return slugData;
    }

    // Nếu không tìm thấy theo slug, thử tìm theo ID
    console.log(`No category found by slug, trying by ID: ${slugOrId}`);
    try {
      const { data: idData, error: idError } = await supabase
        .from('categories')
        .select('*')
        .eq('id', slugOrId)
        .maybeSingle();

      if (!idError && idData) {
        console.log(`Found category by ID: ${slugOrId}`);
        return idData;
      }
    } catch (idError) {
      // Có thể slugOrId không phải là UUID hợp lệ
      console.log(`Error getting category by ID, likely not a valid UUID: ${slugOrId}`);
      console.error(idError);
    }

    console.log(`No category found for slug or ID: ${slugOrId}`);
    return null;
  } catch (error) {
    console.error(`Error in getCategoryBySlugOrId for ${slugOrId}:`, error);
    return null;
  }
}

// Làm mới cache danh mục
export function refreshCategoryCache(): void {
  clearCategoriesCache()
  console.log('Category cache cleared')
} 